import { useMemo } from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import { getChartColors } from '../utils/chartConfig';

// Age range buckets
const AGE_RANGES = [
  { label: '0-5 yr', min: 0, max: 5 },
  { label: '5-10 yr', min: 5, max: 10 },
  { label: '10-15 yr', min: 10, max: 15 },
  { label: '15+ yr', min: 15, max: 100 },
];

// Equipment types
const EQUIPMENT_TYPES = ['Chiller', 'RTU', 'CRAC', 'Split'];

// Equipment type icons
const equipmentIcons = {
  Chiller: '❄️',
  RTU: '🏢',
  CRAC: '🖥️',
  Split: '💨',
};

/**
 * PaybackHeatmap - Equipment Age vs Type Payback Grid
 *
 * Shows payback periods in a grid format:
 * - Rows: Equipment types (Chiller, RTU, CRAC, Split)
 * - Columns: Age ranges (0-5, 5-10, 10-15, 15+ years)
 * - Cell color: Green (fast) → Yellow (medium) → Orange (slow)
 *
 * @param {Array} equipment - Individual equipment results from ROI calculations
 */
export default function PaybackHeatmap({ equipment }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);

  /**
   * Get color based on payback months
   * Fast (<12mo) = green, Medium (12-24mo) = yellow, Slow (>24mo) = orange
   */
  const getPaybackColor = (months) => {
    if (months === null || months === undefined) {
      return {
        bg: 'rgba(140, 155, 165, 0.1)',
        border: 'rgba(140, 155, 165, 0.3)',
        text: colors.grey,
      };
    }

    if (months <= 12) {
      return {
        bg: 'rgba(78, 205, 196, 0.2)',
        border: 'rgba(78, 205, 196, 0.5)',
        text: colors.teal,
      };
    }

    if (months <= 24) {
      return {
        bg: 'rgba(197, 232, 125, 0.2)',
        border: 'rgba(197, 232, 125, 0.5)',
        text: colors.primaryLight,
      };
    }

    return {
      bg: 'rgba(255, 107, 53, 0.2)',
      border: 'rgba(255, 107, 53, 0.5)',
      text: colors.orange,
    };
  };
  // Aggregate payback by type and age
  const heatmapData = useMemo(() => {
    if (!equipment || !Array.isArray(equipment) || equipment.length === 0) {
      return null;
    }

    // Initialize grid
    const grid = {};
    EQUIPMENT_TYPES.forEach((type) => {
      grid[type] = {};
      AGE_RANGES.forEach((range) => {
        grid[type][range.label] = {
          totalPayback: 0,
          count: 0,
          avgPayback: null,
        };
      });
    });

    // Populate grid from equipment data
    equipment.forEach((item) => {
      const type = item.equipment_type;
      const age = item.age || 0;

      if (!EQUIPMENT_TYPES.includes(type)) return;

      // Find age range
      const range = AGE_RANGES.find((r) => age >= r.min && age < r.max);
      if (!range) return;

      const paybackMonths = item.payback_months || item.simple_payback_months;
      if (paybackMonths && paybackMonths > 0) {
        grid[type][range.label].totalPayback += paybackMonths;
        grid[type][range.label].count += 1;
      }
    });

    // Calculate averages
    EQUIPMENT_TYPES.forEach((type) => {
      AGE_RANGES.forEach((range) => {
        const cell = grid[type][range.label];
        if (cell.count > 0) {
          cell.avgPayback = cell.totalPayback / cell.count;
        }
      });
    });

    // Determine which equipment types have data (dynamic filtering)
    const activeTypes = EQUIPMENT_TYPES.filter((type) =>
      AGE_RANGES.some((range) => grid[type][range.label].count > 0)
    );

    return { grid, activeTypes };
  }, [equipment]);

  // If no data or no active equipment types, hide component
  if (!heatmapData || heatmapData.activeTypes.length === 0) {
    return null;
  }

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          True Payback by Equipment Age
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comprehensive payback including all 9 value components (energy savings + avoided costs)
        </Typography>
      </Box>

      {/* Heatmap Container */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #A0CE4E, #4ECDC4)',
            borderRadius: '8px 8px 0 0',
          },
        }}
      >
        {/* Header Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '120px repeat(4, 1fr)',
            gap: 1,
            mb: 2,
          }}
        >
          <Box /> {/* Empty corner cell */}
          {AGE_RANGES.map((range) => (
            <Typography
              key={range.label}
              variant="caption"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                color: colors.grey,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {range.label}
            </Typography>
          ))}
        </Box>

        {/* Data Rows - Only show equipment types that exist in the data */}
        {heatmapData.activeTypes.map((type) => (
          <Box
            key={type}
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px repeat(4, 1fr)',
              gap: 1,
              mb: 1,
            }}
          >
            {/* Row Label */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: '1.25rem' }}>
                {equipmentIcons[type]}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {type}
              </Typography>
            </Box>

            {/* Data Cells */}
            {AGE_RANGES.map((range) => {
              const cell = heatmapData.grid[type][range.label];
              const colors = getPaybackColor(cell.avgPayback);

              return (
                <Tooltip
                  key={range.label}
                  title={
                    cell.count > 0
                      ? `${cell.count} unit${cell.count > 1 ? 's' : ''} • Avg: ${cell.avgPayback.toFixed(1)} months`
                      : 'No data'
                  }
                  arrow
                >
                  <Box
                    sx={{
                      py: 2,
                      px: 1,
                      borderRadius: 1,
                      background: colors.bg,
                      border: `1px solid ${colors.border}`,
                      textAlign: 'center',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: `0 4px 12px ${colors.bg}`,
                      },
                    }}
                  >
                    {cell.avgPayback !== null ? (
                      <>
                        <Typography
                          sx={{
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: colors.text,
                            fontFamily: '"JetBrains Mono", monospace',
                            lineHeight: 1,
                          }}
                        >
                          {cell.avgPayback.toFixed(0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.grey,
                            display: 'block',
                            mt: 0.5,
                          }}
                        >
                          mo
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.grey,
                          fontStyle: 'italic',
                        }}
                      >
                        —
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}

        {/* Legend */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mt: 3,
            pt: 2,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                background: 'rgba(78, 205, 196, 0.3)',
                border: '1px solid rgba(78, 205, 196, 0.5)',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Fast (&lt;12 mo)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                background: 'rgba(197, 232, 125, 0.3)',
                border: '1px solid rgba(197, 232, 125, 0.5)',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Medium (12-24 mo)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                background: 'rgba(255, 107, 53, 0.3)',
                border: '1px solid rgba(255, 107, 53, 0.5)',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Slow (&gt;24 mo)
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
